/**
 *
 * Message
 *
 */

import React, { useMemo, useCallback, useState } from 'react';
// import PropTypes from 'prop-types';
import classNames from 'classnames';
// styles
import './style.scss';

// layout components
import ErrorBoundary from 'engine/ErrorBoundary';

// antd component
import { Button, Typography, Tooltip } from 'antd';

// assets
import CustomIcons from 'components/CustomIcons';
import { EyeOutlined } from '@ant-design/icons';

// Communication components
import { MediaPlayer } from 'components/Audio';

// utils
import useDeviceDetect from 'utils/useDeviceDetect';
import { getObjId, truncate } from 'utils/helpers/helper.general';
import { convertTimeForChat } from 'utils/moment/moment.utils';
import { USER_TYPES } from 'utils/helpers/helper.constant';
// hooks
import { useMounted } from 'utils/hooks';
// global user
import { useGetLocalUser } from 'engine/Contexts/User.context';

function Message({
  message,
  // actions
  setReply,
  onReply,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    'message-wrapper',
    message?.senderRole == 'admin' && 'admin-message',
    message?.senderRole == 'teacher' && 'teacher-message',
    message?.senderRole == 'parent' && 'parent-message',
    className,
  );

  const { isMobile } = useDeviceDetect();

  const mounted = useMounted();
  const localUser = useGetLocalUser()();

  const [visibilityExplanation, setVisibilityExplanation] = useState(false);

  const renderAudio = useMemo(() => {
    const audioTrack = message?.media?.find(el => el?.type == 'audio');
    return (
      !!audioTrack && <MediaPlayer key={message?._id} url={audioTrack?.url} />
    );
  }, [mounted, message?._id, message.content]);

  const renderMessageHeader = useMemo(() => {
    return (
      <div className="message-item__header">
        <div
          className={classNames(
            'message-item__header-fullName',
            message?.senderRole,
          )}
        >
          {!!message?.senderRole && (
            <span className="role-label">
              {USER_TYPES[(message?.senderRole)]}
            </span>
          )}
          <span className="name">
            {message?.sender?.description?.firstname}{' '}
            {message?.sender?.description?.lastname}
          </span>
          {message?.senderRole == 'admin' && (
            <CustomIcons className="admin" type="admin" />
          )}
          {message?.senderRole == 'parent' && (
            <CustomIcons className="admin" type="parent-v2" />
          )}
          {message?.senderRole == 'teacher' && (
            <CustomIcons className="teacher" type="teacher-v2" />
          )}
        </div>
        <span className="message-item__header-date">
          {convertTimeForChat(message?.sentDate)}
        </span>
      </div>
    );
  }, [message?._id, localUser?.role]);

  const rerenderMessageReply = useCallback(() => {
    return (
      <>
        {message?.replayOn && (
          <div
            id={getObjId(message?.replayOn)}
            className="message-item__reply"
            onClick={() => !!onReply && onReply(getObjId(message?.replayOn))}
          >
            <CustomIcons
              type="reply-left"
              className="message-item__reply-icon"
            />
            <div className="message-item__reply-content">
              <span
                className={classNames(
                  'reply-name',
                  message?.replayOn?.senderRole,
                )}
              >
                <span className="name">
                  {message?.replayOn?.sender?.description?.firstname}{' '}
                  {message?.replayOn?.sender?.description?.lastname}
                </span>
                {message?.replayOn?.senderRole == 'admin' && (
                  <CustomIcons className="admin" type="admin" />
                )}
                {message?.replayOn?.senderRole == 'teacher' && (
                  <CustomIcons className="teacher" type="teacher-v2" />
                )}
              </span>
              <div
                className="message-item__reply-text"
                dangerouslySetInnerHTML={{
                  __html: message?.replayOn?.content
                    ? truncate(message?.replayOn?.content, 50)
                    : 'Voice message',
                }}
              />
            </div>
          </div>
        )}
        {renderMessage}
      </>
    );
  }, [message?.replayOn, onReply, renderAudio, renderMessage]);

  const renderMessage = useMemo(() => {
    return (
      <div
        className={classNames(
          'message-item__content',
          message?.senderRole,
          message?.replayOn && 'reply-message',
        )}
      >
        {message?.content && (
          <div
            className="message-item__text"
            dangerouslySetInnerHTML={{
              __html: message?.content,
            }}
          />
        )}
        {!message?.content && renderAudio}
      </div>
    );
  }, [message?.content, message?._id]);

  const renderMessageVisibility = useMemo(() => {
    return (
      <>
        {message?.visibility !== 'all' && (
          <div
            className="message-item__footer-visibility"
            onClick={() => setVisibilityExplanation(prev => !prev)}
          >
            <EyeOutlined />
            {!!visibilityExplanation && (
              <Typography.Text>
                Ce message est visible uniquement par le parent et par
                l’établissement scolaire
              </Typography.Text>
            )}
          </div>
        )}
      </>
    );
  }, [
    message?._id,
    localUser?.role,
    visibilityExplanation,
    setVisibilityExplanation,
  ]);

  const renderMessageVisibilityMobile = useMemo(() => {
    return (
      <>
        {message?.visibility !== 'all' && (
          <Tooltip
            placement="bottomLeft"
            trigger="click"
            color="#5692bd"
            title="Ce message est visible uniquement par le parent et par l’établissement scolaire"
            className="message-item__footer-visibility"
            getPopupContainer={trigger => trigger.parentElement}
          >
            <EyeOutlined />
          </Tooltip>
        )}
      </>
    );
  }, [
    message?._id,
    message?.visibility,
    localUser?.role,
    visibilityExplanation,
    setVisibilityExplanation,
  ]);

  const renderMessageFooter = useMemo(() => {
    return (
      <div className="message-item__footer">
        {!isMobile ? renderMessageVisibility : renderMessageVisibilityMobile}
        <Button
          icon={<CustomIcons type="comments" />}
          onClick={() => !!message && setReply(message)}
          className="message-item__footer-reply-btn"
        >
          Répondre
        </Button>
      </div>
    );
  }, [message?._id, setReply, isMobile, visibilityExplanation]);

  return (
    message?._id && (
      <div
        id={`${message?._id}-wrapper`}
        className={classNames(
          childClassNames,
          localUser?.role,
          getObjId(message?.sender) == getObjId(localUser) && 'right-message',
        )}
      >
        <ErrorBoundary>
          <div
            key={message?._id + '-item'}
            id={message?._id}
            className="message-item"
          >
            {renderMessageHeader}
            {rerenderMessageReply()}
            {renderMessageFooter}
          </div>
        </ErrorBoundary>
      </div>
    )
  );
}

Message.defaultProps = {};
Message.propTypes = {};

export default Message;
